import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { BookOpen, Building2, Home, Menu } from 'lucide-react';
import Logo from 'app/javascript/components/shared/Logo';
import { ROUTES } from 'app/javascript/constants/routes';

const PUBLIC_LINKS = [
  { label: 'Para você', to: ROUTES.HOME, Icon: Home },
  { label: 'Para equipes', to: ROUTES.COMPANY_LANDING, Icon: Building2 },
  { label: 'Central de Ajuda', to: ROUTES.GUIDE_INDEX, Icon: BookOpen },
] as const;

const Navbar: React.FC = () => {
  const { pathname } = useLocation();
  const menu = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    if (menu.current) menu.current.open = false;
  }, [pathname]);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/95">
      <div className="relative mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 sm:gap-5 sm:px-6">
        <Link to={ROUTES.HOME} reloadDocument aria-label="BlackBelts, início" className="rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400/50">
          <Logo iconClassName="h-8 sm:h-10" />
        </Link>
        <nav className="hidden items-center gap-1 md:flex" aria-label="Navegação principal">
          {PUBLIC_LINKS.map((link) => {
            const active = link.to === ROUTES.GUIDE_INDEX ? pathname.startsWith('/guia') : pathname === link.to;
            return (
              <Link key={link.to} to={link.to} reloadDocument={link.to === ROUTES.HOME || link.to === ROUTES.GUIDE_INDEX}
                aria-current={active ? 'page' : undefined}
                className={`rounded-lg px-3 py-3 text-sm font-medium transition-colors hover:text-yellow-300 focus:outline-none focus:ring-2 focus:ring-yellow-400/50 ${active ? 'text-yellow-400' : 'text-slate-400'}`}>
                {link.label}
              </Link>
            );
          })}
        </nav>
        <a href="/download" className="ml-auto rounded-lg border border-slate-700 px-3 py-3 text-xs font-semibold text-slate-200 transition-colors hover:border-yellow-400 hover:text-yellow-300 focus:outline-none focus:ring-2 focus:ring-yellow-400/50">Baixar o app</a>
        <details ref={menu} className="md:hidden" onKeyDown={(event) => {
          if (event.key === 'Escape' && menu.current) {
            menu.current.open = false;
            menu.current.querySelector('summary')?.focus();
          }
        }}>
          <summary aria-label="Abrir menu" className="flex min-h-11 min-w-11 cursor-pointer list-none items-center justify-center rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-yellow-400/50 [&::-webkit-details-marker]:hidden"><Menu className="size-5" /></summary>
          <nav aria-label="Navegação no celular" className="absolute right-4 top-full mt-2 w-64 rounded-xl border border-slate-700 bg-slate-900 p-2 shadow-xl">
            {PUBLIC_LINKS.map(({ label, to, Icon }) => (
              <Link key={to} to={to} reloadDocument={to === ROUTES.HOME || to === ROUTES.GUIDE_INDEX}
                onClick={() => { if (menu.current) menu.current.open = false; }}
                className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm text-slate-200 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-yellow-400/50"><Icon className="size-4" />{label}</Link>
            ))}
          </nav>
        </details>
      </div>
    </header>
  );
};

export default Navbar;
